import React, { useState, useEffect } from 'react';

const FileExplorer = () => {
  // Load explorer state from localStorage
  const loadExplorerState = () => {
    try {
      const savedState = localStorage.getItem('webTopOS_explorer_state');
      if (savedState) {
        return JSON.parse(savedState);
      }
    } catch (e) {
      console.error('Failed to load explorer state:', e);
    }
    return {
      currentPath: '/',
      viewMode: 'list',
      sortBy: 'name',
      selectedFilePath: null
    };
  };

  const initialState = loadExplorerState();

  const [currentPath, setCurrentPath] = useState(initialState.currentPath);
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [viewMode, setViewMode] = useState(initialState.viewMode); // list or grid
  const [sortBy, setSortBy] = useState(initialState.sortBy); // name, type, or date
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);

  // Save explorer state to localStorage
  useEffect(() => {
    const stateToSave = {
      currentPath,
      viewMode,
      sortBy,
      selectedFilePath: selectedFile?.path || null
    };
    localStorage.setItem('webTopOS_explorer_state', JSON.stringify(stateToSave));
  }, [currentPath, viewMode, sortBy, selectedFile]);

  useEffect(() => {
    // Load files from localStorage or initialize
    const savedFiles = localStorage.getItem('webTopOS_files');
    if (savedFiles) {
      setFiles(JSON.parse(savedFiles));
    } else {
      // Initialize with demo files
      const initialFiles = [
        {
          name: 'Documents',
          type: 'folder',
          path: '/Documents',
          dateCreated: new Date().toISOString(),
          children: [
            { 
              name: 'Welcome.txt', 
              type: 'text', 
              path: '/Documents/Welcome.txt', 
              content: 'Welcome to WebTopOS!',
              dateCreated: new Date().toISOString() 
            },
            { 
              name: 'Notes.md', 
              type: 'markdown', 
              path: '/Documents/Notes.md', 
              content: '# My Notes\n\n- First item\n- Second item',
              dateCreated: new Date().toISOString() 
            }
          ]
        },
        {
          name: 'Pictures',
          type: 'folder',
          path: '/Pictures',
          dateCreated: new Date().toISOString(),
          children: [
            { 
              name: 'sample.jpg', 
              type: 'image', 
              path: '/Pictures/sample.jpg', 
              content: 'https://via.placeholder.com/150',
              dateCreated: new Date().toISOString() 
            }
          ]
        },
        { 
          name: 'README.txt', 
          type: 'text', 
          path: '/README.txt', 
          content: 'This is a virtual file system that saves data in your browser.',
          dateCreated: new Date().toISOString() 
        }
      ];
      setFiles(initialFiles);
      localStorage.setItem('webTopOS_files', JSON.stringify(initialFiles));
    }

    // After loading files, restore selected file if it exists
    if (initialState.selectedFilePath) {
      const findFileByPath = (path, fileList) => {
        for (const file of fileList) {
          if (file.path === path) {
            return file;
          }
          if (file.type === 'folder' && file.children) {
            const found = findFileByPath(path, file.children);
            if (found) return found;
          }
        }
        return null;
      };
      
      const savedSelectedFile = findFileByPath(initialState.selectedFilePath, files);
      if (savedSelectedFile) {
        setSelectedFile(savedSelectedFile);
        setFileContent(savedSelectedFile.content || '');
      }
    }
  }, []);

  const saveFiles = (updatedFiles) => {
    setFiles(updatedFiles);
    localStorage.setItem('webTopOS_files', JSON.stringify(updatedFiles));
  };

  const getCurrentFolderContents = () => {
    if (currentPath === '/') {
      return files;
    }
    
    const pathParts = currentPath.split('/').filter(p => p);
    let currentLevel = files;
    
    for (const part of pathParts) {
      const folder = currentLevel.find(f => f.name === part && f.type === 'folder');
      if (folder && folder.children) {
        currentLevel = folder.children;
      } else {
        return [];
      }
    }
    
    // Sort the contents based on sortBy
    return [...currentLevel].sort((a, b) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      } else if (sortBy === 'type') {
        // Folders come first
        if (a.type === 'folder' && b.type !== 'folder') return -1;
        if (a.type !== 'folder' && b.type === 'folder') return 1;
        return a.type.localeCompare(b.type);
      } else if (sortBy === 'date') {
        return new Date(b.dateCreated) - new Date(a.dateCreated);
      }
      return 0;
    });
  };

  const navigateToFolder = (folderPath) => {
    setCurrentPath(folderPath);
    setSelectedFile(null);
    setFileContent('');
    setEditMode(false);
  };

  const navigateUp = () => {
    if (currentPath === '/') return;
    
    const pathParts = currentPath.split('/').filter(p => p);
    pathParts.pop();
    const newPath = pathParts.length === 0 ? '/' : '/' + pathParts.join('/');
    navigateToFolder(newPath);
  };

  const handleFileClick = (file) => {
    if (file.type === 'folder') {
      navigateToFolder(file.path);
    } else {
      setSelectedFile(file);
      setFileContent(file.content || '');
      setEditMode(false);
    }
  };

  const renderFilePreview = () => {
    if (!selectedFile) return null;
    
    if (editMode && (selectedFile.type === 'text' || selectedFile.type === 'markdown')) {
      return (
        <div className="file-editor" style={{ 
          padding: '10px', 
          height: '100%', 
          display: 'flex',
          flexDirection: 'column',
          gap: '10px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>Editing: {selectedFile.name}</div>
            <button 
              onClick={saveFileChanges}
              style={{ 
                padding: '4px 8px',
                backgroundColor: 'rgba(80, 120, 180, 0.5)',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              <i className="fas fa-save"></i> Save
            </button>
          </div>
          <textarea
            value={fileContent}
            onChange={(e) => setFileContent(e.target.value)}
            style={{
              flexGrow: 1,
              backgroundColor: 'rgba(5, 5, 15, 0.4)',
              color: '#fff',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
              padding: '10px',
              fontFamily: 'monospace',
              resize: 'none'
            }}
          />
        </div>
      );
    }
    
    switch (selectedFile.type) {
      case 'text':
      case 'markdown':
        return (
          <div className="file-preview" style={{ 
            padding: '10px', 
            height: '100%', 
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>{selectedFile.name}</div>
              <button 
                onClick={() => setEditMode(true)}
                style={{ 
                  padding: '4px 8px',
                  backgroundColor: 'rgba(80, 120, 180, 0.3)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                <i className="fas fa-edit"></i> Edit
              </button>
            </div>
            <div style={{ 
              backgroundColor: 'rgba(5, 5, 15, 0.2)',
              padding: '15px',
              borderRadius: '4px',
              whiteSpace: 'pre-wrap',
              color: '#fff',
              flexGrow: 1,
              overflow: 'auto'
            }}>
              {fileContent}
            </div>
          </div>
        );
      case 'image':
        return (
          <div className="file-preview" style={{ padding: '10px', textAlign: 'center' }}>
            <div>{selectedFile.name}</div>
            <img src={fileContent} alt={selectedFile.name} style={{ maxWidth: '100%', marginTop: '10px' }} />
          </div>
        );
      default:
        return <div style={{ color: '#fff' }}>No preview available for this file type</div>;
    }
  };

  const saveFileChanges = () => {
    if (!selectedFile) return;
    
    // Update file content
    const updatedFiles = [...files];
    const pathParts = selectedFile.path.split('/').filter(p => p);
    const fileName = pathParts.pop();
    let currentLevel = updatedFiles;
    
    // Navigate to the containing folder
    for (const part of pathParts) {
      const folderIndex = currentLevel.findIndex(f => f.name === part && f.type === 'folder');
      if (folderIndex !== -1) {
        currentLevel = currentLevel[folderIndex].children;
      }
    }
    
    // Find and update the file
    const fileIndex = currentLevel.findIndex(f => f.name === fileName);
    if (fileIndex !== -1) {
      currentLevel[fileIndex].content = fileContent;
      saveFiles(updatedFiles);
    }
    
    setEditMode(false);
  };

  const createNewFile = () => {
    const fileName = prompt('Enter file name:');
    if (!fileName || fileName.trim() === '') return;
    
    let fileType = 'text';
    if (fileName.endsWith('.md')) fileType = 'markdown';
    else if (/\.(jpe?g|png|gif|bmp)$/i.test(fileName)) fileType = 'image';
    
    const newFile = {
      name: fileName,
      type: fileType,
      path: `${currentPath === '/' ? '' : currentPath}/${fileName}`,
      content: fileType === 'image' ? 'https://via.placeholder.com/150' : '',
      dateCreated: new Date().toISOString()
    };
    
    // Add file to current folder
    if (currentPath === '/') {
      saveFiles([...files, newFile]);
    } else {
      const updatedFiles = [...files];
      const pathParts = currentPath.split('/').filter(p => p);
      let currentLevel = updatedFiles;
      
      for (const part of pathParts) {
        const folderIndex = currentLevel.findIndex(f => f.name === part && f.type === 'folder');
        if (folderIndex !== -1) {
          if (!currentLevel[folderIndex].children) {
            currentLevel[folderIndex].children = [];
          }
          currentLevel = currentLevel[folderIndex].children;
        }
      }
      
      currentLevel.push(newFile);
      saveFiles(updatedFiles);
    }
    
    // Select the new file
    setSelectedFile(newFile);
    setFileContent(newFile.content);
    setEditMode(fileType === 'text' || fileType === 'markdown');
    setIsCreateMenuOpen(false);
  };

  const createNewFolder = () => {
    const folderName = prompt('Enter folder name:');
    if (!folderName || folderName.trim() === '') return;
    
    const newFolder = {
      name: folderName,
      type: 'folder',
      path: `${currentPath === '/' ? '' : currentPath}/${folderName}`,
      children: [],
      dateCreated: new Date().toISOString()
    };
    
    // Add folder to current folder
    if (currentPath === '/') {
      saveFiles([...files, newFolder]);
    } else {
      const updatedFiles = [...files];
      const pathParts = currentPath.split('/').filter(p => p);
      let currentLevel = updatedFiles;
      
      for (const part of pathParts) {
        const folderIndex = currentLevel.findIndex(f => f.name === part && f.type === 'folder');
        if (folderIndex !== -1) {
          if (!currentLevel[folderIndex].children) {
            currentLevel[folderIndex].children = [];
          }
          currentLevel = currentLevel[folderIndex].children;
        }
      }
      
      currentLevel.push(newFolder);
      saveFiles(updatedFiles);
    }
    setIsCreateMenuOpen(false);
  };

  const refreshFileExplorer = () => {
    // Re-load the current folder contents
    const savedFiles = localStorage.getItem('webTopOS_files');
    if (savedFiles) {
      setFiles(JSON.parse(savedFiles));
    }
    // Clear any selected file
    setSelectedFile(null);
    setFileContent('');
    setEditMode(false);
  };

  const currentFolderContents = getCurrentFolderContents();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: 'rgba(10, 10, 20, 0.2)', backdropFilter: 'blur(10px)', color: '#fff' }}>
      <div className="file-explorer-toolbar" style={{ 
        padding: '8px', 
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)', 
        display: 'flex', 
        gap: '8px',
        backgroundColor: 'rgba(20, 20, 40, 0.3)',
        backdropFilter: 'blur(15px)'
      }}>
        <button onClick={navigateUp} style={{ 
          padding: '4px 8px',
          backgroundColor: 'rgba(80, 80, 120, 0.3)',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}>
          <i className="fas fa-arrow-up"></i> Up
        </button>
        
        <div style={{ position: 'relative' }}>
          <button 
            onClick={() => setIsCreateMenuOpen(!isCreateMenuOpen)} 
            style={{ 
              padding: '4px 8px',
              backgroundColor: 'rgba(80, 80, 120, 0.3)',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            <i className="fas fa-plus"></i> New
          </button>
          
          {isCreateMenuOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 100,
              backgroundColor: 'rgba(30, 30, 50, 0.95)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
              padding: '5px 0',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
              minWidth: '150px'
            }}>
              <div 
                onClick={createNewFile}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(80, 80, 120, 0.3)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <i className="fas fa-file-alt"></i> Text File
              </div>
              <div 
                onClick={createNewFolder}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(80, 80, 120, 0.3)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <i className="fas fa-folder"></i> Folder
              </div>
            </div>
          )}
        </div>
        
        <button onClick={refreshFileExplorer} style={{ 
          padding: '4px 8px',
          backgroundColor: 'rgba(80, 80, 120, 0.3)',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}>
          <i className="fas fa-sync"></i> Refresh
        </button>
        
        <div style={{ 
          flexGrow: 1, 
          padding: '4px 8px', 
          border: '1px solid rgba(255, 255, 255, 0.1)', 
          borderRadius: '4px',
          backgroundColor: 'rgba(30, 30, 50, 0.2)',
          backdropFilter: 'blur(5px)',
          color: '#fff'
        }}>
          {currentPath}
        </div>
        
        <div style={{ display: 'flex', gap: '4px' }}>
          <button 
            onClick={() => setViewMode('list')} 
            style={{ 
              padding: '4px 8px',
              backgroundColor: viewMode === 'list' ? 'rgba(80, 80, 180, 0.5)' : 'rgba(80, 80, 120, 0.3)',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            title="List View"
          >
            <i className="fas fa-list"></i>
          </button>
          <button 
            onClick={() => setViewMode('grid')} 
            style={{ 
              padding: '4px 8px',
              backgroundColor: viewMode === 'grid' ? 'rgba(80, 80, 180, 0.5)' : 'rgba(80, 80, 120, 0.3)',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            title="Grid View"
          >
            <i className="fas fa-th"></i>
          </button>
        </div>
      </div>
      
      <div style={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        <div className="file-list" style={{ 
          width: '250px', 
          borderRight: '1px solid rgba(255, 255, 255, 0.1)', 
          overflow: 'auto', 
          padding: '8px',
          backgroundColor: 'rgba(20, 20, 40, 0.2)',
          backdropFilter: 'blur(10px)'
        }}>
          <div style={{ marginBottom: '10px' }}>
            <select 
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '4px 8px',
                backgroundColor: 'rgba(30, 30, 50, 0.2)',
                color: '#fff',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '4px',
                width: '100%'
              }}
            >
              <option value="name">Sort by Name</option>
              <option value="type">Sort by Type</option>
              <option value="date">Sort by Date</option>
            </select>
          </div>
          
          {currentFolderContents.length === 0 ? (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center', 
              color: '#aaa',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '10px'
            }}>
              <i className="fas fa-folder-open" style={{ fontSize: '32px' }}></i>
              <div>This folder is empty</div>
              <button 
                onClick={() => setIsCreateMenuOpen(true)} 
                style={{
                  padding: '6px 12px',
                  backgroundColor: 'rgba(80, 120, 180, 0.3)',
                  border: 'none',
                  borderRadius: '4px',
                  color: '#fff',
                  cursor: 'pointer',
                  marginTop: '10px'
                }}
              >
                <i className="fas fa-plus"></i> Create New Item
              </button>
            </div>
          ) : viewMode === 'list' ? (
            currentFolderContents.map((file, index) => (
              <div 
                key={index}
                className="file-item"
                onClick={() => handleFileClick(file)}
                style={{
                  padding: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '4px',
                  backgroundColor: selectedFile && selectedFile.path === file.path ? 'rgba(80, 80, 120, 0.3)' : 'transparent',
                  color: '#fff',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => {
                  if (!selectedFile || selectedFile.path !== file.path) {
                    e.currentTarget.style.backgroundColor = 'rgba(50, 50, 80, 0.2)';
                  }
                }}
                onMouseOut={(e) => {
                  if (!selectedFile || selectedFile.path !== file.path) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <i 
                  className={`fas fa-${file.type === 'folder' ? 'folder' : 
                    file.type === 'image' ? 'image' : 
                    file.type === 'markdown' ? 'file-code' : 'file-alt'}`} 
                  style={{ 
                    marginRight: '8px', 
                    color: file.type === 'folder' ? '#4a90e2' : 
                      file.type === 'image' ? '#e24a75' :
                      file.type === 'markdown' ? '#4ae29a' : '#aaa'
                  }}
                ></i>
                {file.name}
              </div>
            ))
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
              gap: '15px',
              padding: '5px'
            }}>
              {currentFolderContents.map((file, index) => (
                <div 
                  key={index}
                  className="file-item-grid"
                  onClick={() => handleFileClick(file)}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    borderRadius: '8px',
                    padding: '10px 5px',
                    backgroundColor: selectedFile && selectedFile.path === file.path ? 'rgba(80, 80, 120, 0.3)' : 'transparent',
                    color: '#fff',
                    transition: 'background-color 0.2s',
                    textAlign: 'center'
                  }}
                  onMouseOver={(e) => {
                    if (!selectedFile || selectedFile.path !== file.path) {
                      e.currentTarget.style.backgroundColor = 'rgba(50, 50, 80, 0.2)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!selectedFile || selectedFile.path !== file.path) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '5px'
                  }}>
                    <i 
                      className={`fas fa-${file.type === 'folder' ? 'folder' : 
                        file.type === 'image' ? 'image' : 
                        file.type === 'markdown' ? 'file-code' : 'file-alt'}`} 
                      style={{ 
                        fontSize: '24px',
                        color: file.type === 'folder' ? '#4a90e2' : 
                          file.type === 'image' ? '#e24a75' :
                          file.type === 'markdown' ? '#4ae29a' : '#aaa'
                      }}
                    ></i>
                  </div>
                  <div style={{
                    fontSize: '12px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '100%',
                    wordBreak: 'break-word'
                  }}>
                    {file.name}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="file-preview-pane" style={{ 
          flexGrow: 1, 
          overflow: 'auto',
          backgroundColor: 'rgba(5, 5, 15, 0.2)',
          backdropFilter: 'blur(5px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#888'
        }}>
          {selectedFile ? renderFilePreview() : (
            <div style={{ textAlign: 'center' }}>
              <i className="far fa-file" style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.5 }}></i>
              <div>Select a file to preview</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileExplorer; 